import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { applyPathParams } from '@src-common/api/src/apply-path-params';

import { useBookAnAppointmentModal } from '../hooks/useBookAnAppointmentModal';
import { CountryCode, TCountryCodes } from '../models/country-code';

type TAcademyUrlParams = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const getFullName = (params: TAcademyUrlParams) =>
  [params.firstName, params.lastName].filter(Boolean).join(' ');

// Cal.com prefills the booking form only from its own field names; unknown params are ignored.
// Empty values are dropped so they don't overwrite what the chauffeur types in.
const getCalComQueryParams = (params: TAcademyUrlParams) =>
  Object.fromEntries(
    Object.entries({
      name: getFullName(params),
      email: params.email,
      attendeePhoneNumber: params.phone,
      // Hidden booking question in London CKT; CRM matches the booking to the chauffeur by it.
      chauffeur_id: params.id,
    }).filter(([, value]) => Boolean(value)),
  );

const getHostname = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

const getBookingLink = (baseURL: string, params?: TAcademyUrlParams) => {
  if (!baseURL || !params) {
    return baseURL;
  }

  const hostname = getHostname(baseURL);

  if (hostname === 'cal.com') {
    return applyPathParams(baseURL, getCalComQueryParams(params));
  }

  return baseURL;
};

const useBookingActions = ({
  region,
  driver,
}: {
  region: TCountryCodes;
  driver?: TAcademyUrlParams;
}) => {
  const links = useExternalLinks();
  const { openModal } = useBookAnAppointmentModal();

  if (region === CountryCode.Russia || region === CountryCode.ArabEmirates) {
    return {
      goToExternalLinkAction: getBookingLink(links.bookOneDayAppointment, driver),
    };
  }

  if (region === CountryCode.France) {
    return {
      openModalAction: () => {
        openModal({
          region,
          dayOneLink: getBookingLink(links.bookPreAssessment, driver),
          dayTwoLink: '',
        });
      },
    };
  }

  return {
    openModalAction: () => {
      openModal({
        region,
        dayOneLink: getBookingLink(links.bookTwoDayAppointment?.day1 || '', driver),
        dayTwoLink: getBookingLink(links.bookTwoDayAppointment?.day2 || '', driver),
      });
    },
  };
};

export { useBookingActions, getBookingLink };
